import { useRef, useState } from "react";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import GeneratorForm from "./GeneratorForm";
import LessonDocument from "./LessonDocument";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { freeGenerateActivity, resendFreePackage } from "../lib/freeGenerateApi";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const isTimeoutLikeError = (err) => {
  if (!err) return false;
  if (err.code === "ECONNABORTED") return true;
  const msg = String(err.message || err.toString() || "").toLowerCase();
  if (msg.includes("timed out") || msg.includes("timeout") || msg.includes("network error")) return true;
  if (err?.response?.status === 504) return true;
  return false;
};

// The free lesson generator IS the full generator. Same GeneratorForm,
// same payload, same export — the only additions are an email gate at
// Generate and email delivery of the package PDF.
export default function FreeGenerator() {
  const [pendingForm, setPendingForm] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activity, setActivity] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [grantId, setGrantId] = useState(null);
  const [resending, setResending] = useState(false);
  const resultRef = useRef(null);

  const handleFormGenerate = (form) => {
    setPendingForm(form);
    setEmailError("");
    setModalOpen(true);
  };

  const startGeneration = async () => {
    const clean = (email || "").trim();
    if (!EMAIL_RE.test(clean)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setModalOpen(false);
    setLoading(true);
    setActivity(null);
    setDelivery(null);
    try {
      const res = await freeGenerateActivity(pendingForm, {
        email: clean,
        marketingConsent: consent,
      });
      setActivity(res.activity);
      setDelivery(res.delivery);
      setGrantId(res.grantId);
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      if (isTimeoutLikeError(err)) {
        toast.info("Still working — your lesson is generating. Check your email shortly.");
        return;
      }
      let detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object") detail = detail.message || JSON.stringify(detail);
      toast.error("Could not generate your lesson", {
        description: String(detail || err?.message || "Please try again."),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!grantId) return;
    setResending(true);
    try {
      const res = await resendFreePackage(grantId);
      setDelivery(res);
      if (res?.delivered) toast.success("Sent — check your inbox.");
      else toast.error("Still couldn't send", { description: res?.error || "Please try again." });
    } catch (err) {
      toast.error("Resend failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="free-generator">
      <GeneratorForm onGenerate={handleFormGenerate} loading={loading} hideUnitToggle />

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="free-email-modal" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#3EB489]" /> One free lesson, on us
            </DialogTitle>
            <DialogDescription>
              Enter your email — your lesson shows here and the complete package PDF
              lands in your inbox. No account, no password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <Input
                type="email"
                inputMode="email"
                autoFocus
                placeholder="you@yourschool.ca"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") startGeneration(); }}
                data-testid="free-email-input"
              />
              {emailError && (
                <p className="mt-1.5 text-sm font-semibold text-red-600" data-testid="free-email-error">
                  {emailError}
                </p>
              )}
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="free-consent-label">
              <Checkbox
                checked={consent}
                onCheckedChange={(v) => setConsent(!!v)}
                data-testid="free-consent-checkbox"
                className="mt-0.5"
              />
              <span className="text-sm text-[#4a6984] leading-snug">
                Email me occasional Atomic Edu updates — new features, free resources,
                and Ontario curriculum news. Unsubscribe anytime.
              </span>
            </label>
            <Button
              onClick={startGeneration}
              className="w-full bg-[#3EB489] hover:bg-[#35a07a] text-white font-extrabold"
              data-testid="free-email-submit"
            >
              Generate my lesson
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div ref={resultRef} className="mt-10 space-y-6">
        {loading && !activity && (
          <div
            className="rounded-2xl border-2 border-[#3EB489]/25 bg-white p-6 shadow-sm flex items-center gap-3"
            data-testid="free-generating-banner"
          >
            <Loader2 className="h-5 w-5 animate-spin text-[#3EB489]" />
            <p className="text-[#4a6984] font-semibold">
              Designing your lesson from the Ontario curriculum — this takes about a minute.
            </p>
          </div>
        )}

        {activity && (
          <>
            <div
              className={`rounded-2xl border-2 p-5 shadow-sm ${
                delivery?.delivered
                  ? "border-[#3EB489]/30 bg-[#3EB489]/5"
                  : "border-amber-400/50 bg-amber-50"
              }`}
              data-testid="free-delivery-banner"
            >
              {delivery?.delivered ? (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#3EB489] mt-0.5 shrink-0" />
                  <p className="text-[#1c2d3d] font-semibold" data-testid="free-delivery-sent">
                    Your complete lesson package is on its way to{" "}
                    <span className="font-black">{delivery.email}</span>. The link works for 30 days.
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[#1c2d3d] font-semibold" data-testid="free-delivery-failed">
                      Your lesson generated, but we couldn't email the PDF
                      {delivery?.error ? `: ${delivery.error}` : "."}
                    </p>
                    <Button
                      onClick={handleResend}
                      disabled={resending}
                      variant="outline"
                      size="sm"
                      className="mt-2 font-bold"
                      data-testid="free-resend-button"
                    >
                      {resending ? "Sending…" : "Resend the email"}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {(activity.lesson_markdown || activity.summative_markdown) && (
              <LessonDocument activity={activity} enableRefine={false} hideDownloads />
            )}
          </>
        )}
      </div>
    </div>
  );
}
